// Low-level CSS assembly for styleLoader: rules keyed by selector, per target
// (portrait / landscape / all), and the text, box and button trait groups.
import { must } from "@/common/utils/must";
import objectUtils from "@/common/utils/objectUtils";
import type { StyleTrait } from "@/types/experience";

// Rules by selector, in insertion order; "@font-face" holds a list of faces.
export type CssJs = Record<string, unknown>;
export type CssMap = Record<string, CssJs | CssJs[]>;

export function addStyle(
  target: string,
  css: CssMap,
  selector: string,
  style: string,
  value: unknown,
  targetAll?: boolean,
) {
  if (value == null || style == null) {
    return;
  }
  if (target === "mobile") {
    target = "portrait";
  } else if (target === "computer") {
    target = "landscape";
  }

  if (target === "portrait" || target === "landscape") {
    selector = `.${target} ${selector}`;
  } else if (target === "all") {
    // Target all means explicity use a target.
    if (targetAll === true) {
      addStyle("landscape", css, selector, style, value);
      addStyle("portrait", css, selector, style, value);
      return;
    }
  } else {
    return;
  }

  let cssJs = css[selector];
  if (cssJs == null) {
    cssJs = {};
    css[selector] = cssJs;
  }

  addSelectorValue(cssJs as CssJs, style, value);
}

export function addSelectorValue(cssJs: CssJs, style: string, value: unknown) {
  if (value == null || style == null) {
    return;
  }

  let s = cssJs[style];
  if (s) {
    let a: unknown[] | undefined;
    if (!objectUtils.isArray(s)) {
      a = [];
      a.push(s);
      cssJs[style] = a;
      s = a;
    }
    // `a` is unset when `s` was already an array, so this push throws.
    must(a, "style value list").push(value);
  } else {
    cssJs[style] = value;
  }
}

export function addFontFaceEntry(css: CssMap, value: CssJs) {
  const selector = "@font-face";
  if (value == null) {
    return;
  }

  let cssJs = css[selector];
  if (cssJs == null) {
    cssJs = [];
    css[selector] = cssJs;
  }

  (cssJs as CssJs[]).push(value);
}

export function cssSize(val: unknown): unknown {
  if (isNaN(val as number)) {
    return val;
  } else {
    return `${val}px`;
  }
}

export function addTextTrait(
  target: string,
  css: CssMap,
  selector: string,
  trait: StyleTrait,
  targetAll?: boolean,
) {
  addStyle(target, css, selector, "font-family", trait.fontFamily, targetAll);
  addStyle(target, css, selector, "font-size", trait.fontSize, targetAll);
  addStyle(target, css, selector, "font-weight", trait.fontWeight, targetAll);
  addStyle(target, css, selector, "font-style", trait.fontStyle, targetAll);
  addStyle(target, css, selector, "font-stretch", trait.fontStretch, targetAll);
  addStyle(
    target,
    css,
    selector,
    "letter-spacing",
    trait.letterSpacing,
    targetAll,
  );
  addStyle(target, css, selector, "line-height", trait.lineHeight, targetAll);
  addStyle(target, css, selector, "text-align", trait.textAlign, targetAll);
  addStyle(target, css, selector, "color", trait.color, targetAll);
}

export function addBoxTrait(
  target: string,
  css: CssMap,
  selector: string,
  trait: StyleTrait,
) {
  addStyle(target, css, selector, "margin", trait.margin);
  addStyle(target, css, selector, "padding", trait.padding);
}

export function addButtonTrait(
  target: string,
  css: CssMap,
  selector: string,
  trait: StyleTrait,
  ignore?: { borderRadius?: boolean },
) {
  if (
    trait.borderRadius != null &&
    !(ignore && ignore.borderRadius === false)
  ) {
    const radius = cssSize(trait.borderRadius);
    addStyle(target, css, selector, "border-radius", radius);
    addStyle(
      target,
      css,
      `${selector}.radius-b-left`,
      "border-radius",
      `${radius} 0 0 ${radius}`,
    );
  }

  addStyle(target, css, `${selector} > svg`, "fill", trait.color);
  addStyle(target, css, `${selector} > svg`, "stroke", trait.color);
  addStyle(target, css, `${selector} > .icon-wrapper svg`, "fill", trait.color);
  addStyle(
    target,
    css,
    `${selector} > .icon-wrapper svg`,
    "stroke",
    trait.color,
  );
  addStyle(target, css, selector, "border-color", trait.borderColor);
  addStyle(target, css, selector, "border-style", trait.borderStyle);
  addStyle(target, css, selector, "border-width", cssSize(trait.borderWidth));
  addStyle(target, css, selector, "background-color", trait.backgroundColor);
}

export function addButtonHoverTrait(
  target: string,
  css: CssMap,
  selector: string,
  trait: StyleTrait,
) {
  selector = `${selector}:hover`;
  addButtonTrait(target, css, selector, trait);
  addStyle(
    target,
    css,
    `${selector},${selector} .badge ,${selector} .hover`,
    "background-color",
    trait.backgroundColor,
  );
  addStyle(
    target,
    css,
    `${selector},${selector} .badge ,${selector} .hover`,
    "border-color",
    trait.borderColor,
  );
}
